import api from "./api";

/**
 * Get audit action list with filters (uses /v1/audit/action/list_filter endpoint)
 *
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Items per page (default: 20)
 * @param {string} params.orderBy - Field to order by (default: "action")
 * @param {string} params.orderDirection - Order direction (default: "ASC")
 * @param {Object} params.search - Search object with keyword
 * @returns {Promise<Object>} API response with body containing action list
 * @throws {Error} If the API call fails
 */
export const getAuditActionListFilter = async (params = {}) => {
  const body = {
    page: params.page || 1,
    pageSize: params.pageSize || 20,
    orderBy: params.orderBy || "action",
    orderDirection: params.orderDirection || "ASC",
  };

  if (params.search) {
    if (typeof params.search === "string" && params.search.trim() !== "") {
      body.search = { keyword: params.search.trim() };
    } else if (params.search?.keyword && params.search.keyword.trim() !== "") {
      body.search = { keyword: params.search.keyword.trim() };
    }
  }

  try {
    const response = await api.post("/v1/audit/action/list_filter", body);
    const responseData = response.data;

    if (!responseData) {
      throw new Error("Invalid response from server");
    }

    const statusCode =
      responseData.status_code ||
      responseData.statusCode ||
      response?.status ||
      200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];

    if (statusCode >= 200 && statusCode < 300) {
      return {
        status_code: statusCode,
        statusCode: statusCode,
        msg_code: msgCode,
        msgCode: msgCode,
        request_id:
          responseData.request_id ||
          responseData.requestId ||
          `req-${Date.now()}`,
        requestId:
          responseData.request_id ||
          responseData.requestId ||
          `req-${Date.now()}`,
        body: Array.isArray(responseData.body) ? responseData.body : [],
        meta: responseData.meta || null,
      };
    }

    const errorMessage =
      msgCode.length > 0
        ? msgCode[0].description ||
          msgCode[0].code ||
          "An error occurred while fetching audit actions."
        : "An error occurred while fetching audit actions.";

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };
    throw error;
  } catch (error) {
    if (error.response) {
      throw error;
    }

    if (error.request) {
      throw new Error(
        "Unable to connect to the server. Please check your internet connection and try again."
      );
    }

    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      throw new Error(
        "Request timeout. The server took too long to respond. Please try again."
      );
    }

    throw error;
  }
};

/**
 * Get audit section/resource list with filters (uses /v1/audit/section/list_filter endpoint)
 *
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Items per page (default: 20)
 * @param {string} params.orderBy - Field to order by (default: "business_entity_name")
 * @param {string} params.orderDirection - Order direction (default: "ASC")
 * @param {Object} params.search - Search object with keyword
 * @returns {Promise<Object>} API response with body containing section list
 * @throws {Error} If the API call fails
 */
export const getAuditSectionListFilter = async (params = {}) => {
  const orderBy = Array.isArray(params.orderBy)
    ? params.orderBy[0]
    : params.orderBy;
  const orderDirection = Array.isArray(params.orderDirection)
    ? params.orderDirection[0]
    : params.orderDirection;

  const body = {
    page: params.page || 1,
    pageSize: params.pageSize || 20,
    orderBy: orderBy || "business_entity_name",
    orderDirection: orderDirection || "ASC",
  };

  if (params.search) {
    if (typeof params.search === "string" && params.search.trim() !== "") {
      body.search = { keyword: params.search.trim() };
    } else if (params.search?.keyword && params.search.keyword.trim() !== "") {
      body.search = { keyword: params.search.keyword.trim() };
    }
  }

  try {
    const response = await api.post("/v1/audit/section/list_filter", body);
    const responseData = response.data;

    if (!responseData) {
      throw new Error("Invalid response from server");
    }

    const statusCode =
      responseData.status_code ||
      responseData.statusCode ||
      response?.status ||
      200;
    const msgCode = responseData.msg_code || responseData.msgCode || [];

    if (statusCode >= 200 && statusCode < 300) {
      return {
        status_code: statusCode,
        statusCode: statusCode,
        msg_code: msgCode,
        msgCode: msgCode,
        request_id:
          responseData.request_id ||
          responseData.requestId ||
          `req-${Date.now()}`,
        requestId:
          responseData.request_id ||
          responseData.requestId ||
          `req-${Date.now()}`,
        body: Array.isArray(responseData.body) ? responseData.body : [],
        meta: responseData.meta || null,
      };
    }

    const errorMessage =
      msgCode.length > 0
        ? msgCode[0].description ||
          msgCode[0].code ||
          "An error occurred while fetching audit sections."
        : "An error occurred while fetching audit sections.";

    const error = new Error(errorMessage);
    error.response = {
      data: responseData,
      status: statusCode,
    };
    throw error;
  } catch (error) {
    if (error.response) {
      throw error;
    }

    if (error.request) {
      throw new Error(
        "Unable to connect to the server. Please check your internet connection and try again."
      );
    }

    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      throw new Error(
        "Request timeout. The server took too long to respond. Please try again."
      );
    }

    throw error;
  }
};
